import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faScissors } from '@fortawesome/free-solid-svg-icons';
import PdfPageCanvas from './PdfPageCanvas';

const THUMBNAIL_WIDTH = 120;

const PdfSplitterFilmstrip = ({
  pdfDoc,
  numPages,
  pageGroupIndex,
  cutAfter,
  excludedPages,
  selectedPage,
  onSelectPage,
  onToggleCut,
  onToggleExclude,
}) => {
  const stripRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    const strip = stripRef.current;
    const selected = selectedRef.current;
    if (!strip || !selected) return;
    const stripRect = strip.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    const above = selectedRect.top - stripRect.top;
    const below = selectedRect.bottom - stripRect.bottom;
    if (above < 0) strip.scrollBy({ top: above, behavior: 'smooth' });
    else if (below > 0) strip.scrollBy({ top: below, behavior: 'smooth' });
  }, [selectedPage]);

  return (
    <div className="pdf-splitter-filmstrip" ref={stripRef}>
      {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => {
        const segIndex = pageGroupIndex[pageNumber] ?? 0;
        const isCut = cutAfter.has(pageNumber);
        const isExcluded = excludedPages.has(pageNumber);
        const isLast = pageNumber === numPages;
        const isInView = pageNumber === selectedPage || (selectedPage < numPages && pageNumber === selectedPage + 1);
        return (
          <div
            key={pageNumber}
            className="pdf-splitter-page-wrapper"
            ref={pageNumber === selectedPage ? selectedRef : null}
          >
            <div
              className={`pdf-splitter-page ${isExcluded ? 'pdf-splitter-page-excluded' : `pdf-splitter-seg-${segIndex % 6}`}`}
              role="button"
              tabIndex={0}
              title={`Show the boundary after page ${pageNumber}`}
              onClick={() => onSelectPage(pageNumber)}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
                  event.preventDefault();
                  onSelectPage(pageNumber);
                }
              }}
            >
              <label
                className="pdf-splitter-page-checkbox"
                onClick={(event) => event.stopPropagation()}
                title={isExcluded ? `Include page ${pageNumber}` : `Discard page ${pageNumber}`}
              >
                <input
                  type="checkbox"
                  className="config-setting-checkbox"
                  checked={!isExcluded}
                  onChange={() => onToggleExclude(pageNumber)}
                />
              </label>
              <PdfPageCanvas pdfDoc={pdfDoc} pageNumber={pageNumber} width={THUMBNAIL_WIDTH} />
              <div className={`pdf-splitter-page-label ${isInView ? 'pdf-splitter-page-label-viewing' : ''} ${isExcluded ? 'pdf-splitter-page-label-excluded' : ''}`}>
                {isInView && <FontAwesomeIcon icon={faEye} />}
                Page {pageNumber}
              </div>
            </div>
            {!isLast && (
              <button
                type="button"
                className={`pdf-splitter-cut-button ${isCut ? 'pdf-splitter-cut-active' : ''}`}
                onClick={() => onToggleCut(pageNumber)}
                aria-pressed={isCut}
                title={isCut ? `Remove split after page ${pageNumber}` : `Split after page ${pageNumber}`}
              >
                <FontAwesomeIcon icon={faScissors} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PdfSplitterFilmstrip;
